/**
 * WelcomeLetterEditor — Gmail-style WYSIWYG for the Draft Board Welcome Letter
 * body. One toolbar row (like Gmail): Font, Size, Bold, Italic, Underline, Text
 * colour, Align, Numbered/Bulleted list, Indent less/more, Quote, Strikethrough,
 * Remove formatting, Link (dialog: "Text to display" + "Web address"; click a
 * link to edit/remove). Non-technical users NEVER see raw HTML. Body is stored
 * as HTML with email-safe INLINE styles; legacy plain text auto-converts to <p>
 * on load (handled by the parent). Wildcard buttons insert #tokens# at the
 * cursor via the imperative `insertWildcard` handle. No colours/copy hardcoded.
 * TipTap extensions pinned to 3.26.0; FontSize + Indent are small in-file
 * extensions (no extra package).
 */
import React, { useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import { FontFamily } from '@tiptap/extension-font-family';
import { Extension } from '@tiptap/core';
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough, Palette,
  AlignLeft, AlignCenter, AlignRight, ListOrdered, List, IndentDecrease, IndentIncrease,
  Quote, RemoveFormatting, Link as LinkIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const FONTS = [
  { label: 'Sans Serif', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Serif', value: "Georgia, 'Times New Roman', serif" },
  { label: 'Fixed width', value: "'Courier New', monospace" },
];
const SIZES = [
  { label: 'Small', value: '13px' },
  { label: 'Normal', value: '' },
  { label: 'Large', value: '20px' },
  { label: 'Huge', value: '28px' },
];
const INDENT_STEP = 24;
const MAX_INDENT = 24 * 6;

// Adds a font-size to the textStyle mark (email-safe inline style).
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [{
      types: ['textStyle'],
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize || null,
          renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size:${attrs.fontSize}` } : {}),
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) =>
        (size ? chain().setMark('textStyle', { fontSize: size }).run()
              : chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run()),
    };
  },
});

// Block indent via inline padding-left on paragraphs/headings (email-safe).
const Indent = Extension.create({
  name: 'indent',
  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading'],
      attributes: {
        indent: {
          default: 0,
          parseHTML: (el) => parseInt(el.style.paddingLeft, 10) || 0,
          renderHTML: (attrs) => (attrs.indent ? { style: `padding-left:${attrs.indent}px` } : {}),
        },
      },
    }];
  },
  addCommands() {
    const step = (dir) => ({ state, dispatch }) => {
      const { from, to } = state.selection;
      let tr = state.tr; let changed = false;
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (['paragraph', 'heading'].includes(node.type.name)) {
          const cur = node.attrs.indent || 0;
          const next = Math.max(0, Math.min(MAX_INDENT, cur + dir * INDENT_STEP));
          if (next !== cur) { tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next }); changed = true; }
        }
      });
      if (changed && dispatch) dispatch(tr);
      return changed;
    };
    return { indentMore: () => step(1), indentLess: () => step(-1) };
  },
});

const Btn = ({ onClick, active, title, testId, children }) => (
  <button type="button" title={title} data-testid={testId} onClick={onClick}
    className={`h-7 min-w-7 px-1.5 rounded text-slate-600 flex items-center justify-center hover:bg-slate-200 ${active ? 'bg-slate-300 text-slate-900' : ''}`}>
    {children}
  </button>
);
const Sep = () => <span className="w-px h-5 bg-slate-300 mx-0.5" />;

const WelcomeLetterEditor = forwardRef(function WelcomeLetterEditor({ value, onChange }, ref) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Indent,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      handleClickOn: (view, pos, node, nodePos, event) => {
        // Gmail behaviour: click a link to edit/remove it.
        const el = event.target;
        if (el && el.closest && el.closest('a')) { setTimeout(openLinkDialog, 0); return true; }
        return false;
      },
    },
  });

  useImperativeHandle(ref, () => ({
    insertWildcard: (token) => editor && editor.chain().focus().insertContent(`#${token}#`).run(),
  }), [editor]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || '', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const openLinkDialog = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const sel = editor.state.doc.textBetween(from, to, ' ');
    setLinkText(sel || '');
    setLinkUrl(editor.getAttributes('link').href || '');
    setLinkOpen(true);
  };

  const escAttr = (s) => (s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const escTxt = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const applyLink = () => {
    let href = (linkUrl || '').trim();
    if (!href) { setLinkOpen(false); return; }
    if (!/^(https?:|mailto:|\/|#)/i.test(href)) href = 'https://' + href;
    const text = (linkText || '').trim() || href;
    const chain = editor.chain().focus();
    if (editor.isActive('link')) chain.extendMarkRange('link');
    chain.deleteSelection().insertContent(`<a href="${escAttr(href)}">${escTxt(text)}</a>&nbsp;`).run();
    setLinkOpen(false);
  };
  const removeLink = () => { editor.chain().focus().extendMarkRange('link').unsetLink().run(); setLinkOpen(false); };

  if (!editor) return null;

  const curFont = editor.getAttributes('textStyle').fontFamily || '';
  const curSize = editor.getAttributes('textStyle').fontSize || '';
  const curColor = editor.getAttributes('textStyle').color || '#1e293b';
  const selCls = 'h-7 rounded border border-slate-300 bg-white text-xs px-1 text-slate-700';

  return (
    <div data-testid="welcome-editor" className="border border-slate-300 rounded">
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 bg-slate-50 border-b border-slate-200 rounded-t">
        <select title="Font" data-testid="wl-font" className={selCls} value={curFont}
          onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}>
          <option value="">Font</option>
          {FONTS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <select title="Size" data-testid="wl-size" className={selCls} value={curSize}
          onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}>
          {SIZES.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
        </select>
        <Sep />
        <Btn testId="wl-bold" title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
        <Btn testId="wl-italic" title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn testId="wl-underline" title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></Btn>
        <label title="Text colour" data-testid="wl-color-label" className="h-7 w-7 rounded flex items-center justify-center text-slate-600 hover:bg-slate-200 cursor-pointer relative">
          <Palette className="h-4 w-4" />
          <input type="color" value={curColor} data-testid="wl-color" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            className="absolute inset-0 opacity-0 cursor-pointer" />
        </label>
        <Sep />
        <Btn testId="wl-align-left" title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}><AlignLeft className="h-4 w-4" /></Btn>
        <Btn testId="wl-align-center" title="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}><AlignCenter className="h-4 w-4" /></Btn>
        <Btn testId="wl-align-right" title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}><AlignRight className="h-4 w-4" /></Btn>
        <Sep />
        <Btn testId="wl-ol" title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn testId="wl-ul" title="Bulleted list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
        <Btn testId="wl-indent-less" title="Indent less" onClick={() => editor.chain().focus().indentLess().run()}><IndentDecrease className="h-4 w-4" /></Btn>
        <Btn testId="wl-indent-more" title="Indent more" onClick={() => editor.chain().focus().indentMore().run()}><IndentIncrease className="h-4 w-4" /></Btn>
        <Btn testId="wl-quote" title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Btn>
        <Btn testId="wl-strike" title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Btn>
        <Sep />
        <Btn testId="wl-clear" title="Remove formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><RemoveFormatting className="h-4 w-4" /></Btn>
        <Btn testId="wl-link" title="Insert link" active={editor.isActive('link')} onClick={openLinkDialog}><LinkIcon className="h-4 w-4" /></Btn>
      </div>
      <div className="min-h-[220px] max-h-[50vh] overflow-y-auto p-3 prose prose-sm max-w-none text-sm welcome-editor-surface"
        data-testid="welcome-editor-surface">
        <EditorContent editor={editor} />
      </div>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md" data-testid="wl-link-dialog">
          <DialogHeader><DialogTitle>Edit link</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Text to display</Label>
              <Input value={linkText} onChange={(e) => setLinkText(e.target.value)} placeholder="Sabres Conference Team Player Document" data-testid="wl-link-text" />
            </div>
            <div>
              <Label className="text-xs">Web address</Label>
              <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" data-testid="wl-link-url" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {editor.isActive('link') && <Button variant="outline" onClick={removeLink} data-testid="wl-link-remove">Remove</Button>}
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button onClick={applyLink} data-testid="wl-link-apply">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default WelcomeLetterEditor;
